import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
} from '@mui/material';
import { FactCheck as FactCheckIcon } from '@mui/icons-material';
import { SV } from '../../automation/stratovaiTestIds';
import RequirementAnalysisSummary from './RequirementAnalysisSummary';
import RequirementAnalysisSections from './RequirementAnalysisSections';
import { REQUIREMENT_ANALYSIS_COPY as C } from './requirementAnalysisUiConfig';

function resolveAnalyzedItemTitle(sourceItem) {
  return (
    sourceItem?.content?.name ||
    sourceItem?.content?.title ||
    sourceItem?.title ||
    C.defaultItemTitle
  );
}

/**
 * Requirement Analysis — clarity, completeness, and story/design consistency (no test generation / impact).
 */
const RequirementAnalysisModal = ({ open, onClose, loading, data, sourceItem, onRefresh }) => {
  const title = resolveAnalyzedItemTitle(sourceItem);
  const analyzedLabel = data?.analyzed_at
    ? new Date(data.analyzed_at).toLocaleString()
    : null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ 'data-testid': SV.pk.requirementAnalysisModal }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <FactCheckIcon color="primary" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="h6" component="span">
              {C.modalTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {C.modalSubtitle}
            </Typography>
            {data && (data.cached || analyzedLabel) ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {[data.cached ? C.cachedResultLabel : null, analyzedLabel ? `${C.analyzedAtPrefix} ${analyzedLabel}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {C.analyzingPrefix} &quot;{title}&quot;...
            </Typography>
            <LinearProgress />
          </Box>
        ) : data ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <RequirementAnalysisSummary data={data} />
            <RequirementAnalysisSections data={data} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {C.emptyNoAnalysisData}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {onRefresh && data && !loading ? (
          <Button onClick={() => onRefresh(sourceItem)} color="primary">
            {C.refreshAnalysis}
          </Button>
        ) : null}
        <Button onClick={onClose} disabled={loading} data-testid={SV.pk.requirementAnalysisClose}>
          {C.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequirementAnalysisModal;
